import { useEffect, useState } from "react";
import { api, ron, EXPENSE_CATEGORIES, FIXED_COST_CATEGORIES, VAT_STATUS, VAT_LABEL, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { formatLei } from "@/lib/money";
import { Trash2, Receipt, Download, Paperclip, Building2, Plus, Repeat } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import DualMoneyInput from "@/components/DualMoneyInput";
import { RateBadge } from "@/components/RateBadge";
import DateInput from "@/components/DateInput";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";

function LeiHint({ amount, doc }) {
  const { rate } = useCurrency();
  if (doc && doc.original_currency === "RON" && doc.original_amount != null) {
    return <span className="text-[10px] text-slate-400 tabular block leading-none mt-0.5">{formatLei(doc.original_amount)} (fix)</span>;
  }
  const r = doc?.rate_locked && doc?.rate_at_entry ? doc.rate_at_entry : rate;
  if (!amount || !r) return null;
  const lei = Math.round(Number(amount) * r);
  return <span className="text-[10px] text-slate-400 tabular block leading-none mt-0.5">≈ {new Intl.NumberFormat("ro-RO").format(lei)} lei</span>;
}

function VatBadge({ status }) {
  if (!status) return null;
  const cls = status === "cu_tva" ? "bg-blue-50 text-blue-700"
    : status === "fara_tva" ? "bg-amber-50 text-amber-700"
    : "bg-slate-100 text-slate-600";
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cls}`}>{VAT_LABEL[status] || status}</span>
  );
}

function InvoiceDownload({ path }) {
  if (!path) return null;
  async function dl() {
    try {
      const r = await api.get("/files/serve", { params: { path }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "factura";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Descărcare eșuată"); }
  }
  return (
    <button onClick={dl} className="text-slate-400 hover:text-[#0047FF]" title="Descarcă factura">
      <Download className="w-4 h-4" />
    </button>
  );
}

function CarExpensesTab() {
  const [items, setItems] = useState([]);
  const [cars, setCars] = useState({});
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  async function load() {
    const [{ data }, c] = await Promise.all([api.get("/expenses"), api.get("/cars")]);
    setItems(data);
    setCars(Object.fromEntries(c.data.map((x) => [x.id, x])));
  }
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
  }, []);

  const total = items.reduce((s, e) => s + (e.amount || 0), 0);

  async function remove(id) { await api.delete(`/expenses/${id}`); load(); }

  return (
    <div className="space-y-3">
      <div className="text-sm tabular text-right">Total cheltuieli pe mașini: <strong>{ron(total)}</strong></div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="p-10 text-center">
            <Receipt className="w-10 h-10 mx-auto text-slate-300" />
            <div className="mt-3 font-display text-lg">Nicio cheltuială</div>
            <div className="text-sm text-slate-500 mt-1">Adaugă din fișa unei mașini sau cu butonul +.</div>
          </div>
        )}
        {items.map((e) => {
          const car = cars[e.car_id];
          return (
            <div key={e.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-2">
                  {e.category}
                  {e.invoice_path && <span title="Cu factură" className="text-emerald-600"><Paperclip className="w-3 h-3" /></span>}
                  {e.vat_status && <VatBadge status={e.vat_status} />}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {car ? `${car.make} ${car.model}` : "Fără mașină"}
                  {e.description ? " · " + e.description : ""}
                  {" · "}{formatDate(e.date)}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <InvoiceDownload path={e.invoice_path} />
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5"><RateBadge doc={e} testid={`expense-rate-badge-${e.id}`} /><span className="tabular text-sm font-medium">{ron(e.amount)}</span></span>
                  <LeiHint amount={e.amount} doc={e} />
                </div>
                {isAdmin && (
                  <button onClick={() => remove(e.id)} className="text-slate-400 hover:text-red-600" data-testid={`expense-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RecurringTemplates() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "Chirie", amount: "", description: "", recurring_day: 1, vat_status: "cu_tva", active: true, original_amount: null, original_currency: null, rate_at_entry: null });
  const [open, setOpen] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/recurring-templates");
      setItems(data);
    } catch {}
  }
  useEffect(() => { load(); }, []);

  async function add(e) {
    e.preventDefault();
    if (!form.amount) return;
    try {
      await api.post("/recurring-templates", {
        ...form,
        amount: Number(form.amount),
        recurring_day: Math.max(1, Math.min(28, Number(form.recurring_day) || 1)),
      });
      toast.success("Cost recurent salvat — se generează automat la ziua " + form.recurring_day);
      setForm({ category: "Chirie", amount: "", description: "", recurring_day: 1, vat_status: "cu_tva", active: true, original_amount: null, original_currency: null, rate_at_entry: null });
      setOpen(false);
      load();
      window.dispatchEvent(new CustomEvent("data:refresh"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function toggleActive(t) {
    await api.put(`/recurring-templates/${t.id}`, { ...t, active: !t.active });
    load();
  }

  async function remove(id) {
    if (!window.confirm("Ștergi acest cost recurent? (instanțele deja generate rămân.)")) return;
    await api.delete(`/recurring-templates/${id}`);
    load();
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Auto-generare lunară</div>
          <h3 className="font-display text-base font-medium flex items-center gap-2"><Repeat className="w-4 h-4" />Costuri recurente ({items.length})</h3>
        </div>
        <button data-testid="rt-toggle-form" onClick={() => setOpen(!open)} className="text-xs text-[#0047FF] hover:underline">
          {open ? "Anulează" : "+ Adaugă recurent"}
        </button>
      </div>

      {open && (
        <form onSubmit={add} className="grid grid-cols-12 gap-2 pt-2 border-t border-slate-100">
          <select data-testid="rt-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="col-span-6 sm:col-span-3 border border-slate-200 rounded-lg px-2 py-2 text-sm">
            {FIXED_COST_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <div className="col-span-12 sm:col-span-4">
            <DualMoneyInput
              testid="rt-money"
              value={form.amount}
              onChange={(v) => setForm({ ...form, amount: v })}
              onMeta={(m) => setForm((prev) => ({ ...prev, ...m }))}
            />
          </div>
          <select data-testid="rt-day" value={form.recurring_day} onChange={(e) => setForm({ ...form, recurring_day: e.target.value })} className="col-span-6 sm:col-span-2 border border-slate-200 rounded-lg px-2 py-2 text-sm">
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>Ziua {d}</option>)}
          </select>
          <select data-testid="rt-vat" value={form.vat_status} onChange={(e) => setForm({ ...form, vat_status: e.target.value })} className="col-span-6 sm:col-span-2 border border-slate-200 rounded-lg px-2 py-2 text-sm">
            {VAT_STATUS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
          <input data-testid="rt-desc" placeholder="Descriere (opțional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-12 sm:col-span-9 border border-slate-200 rounded-lg px-2 py-2 text-sm" />
          <button data-testid="rt-submit" className="col-span-12 sm:col-span-3 bg-[#0047FF] hover:bg-[#003BD3] text-white rounded-lg text-sm py-2">Salvează template</button>
        </form>
      )}

      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic">Niciun cost recurent. Adaugă chirie/leasing/salarii pentru auto-generare lunară.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((t) => (
            <div key={t.id} className="flex items-center justify-between py-2" data-testid={`rt-row-${t.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {t.category}
                  <span className="text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">Auto · ziua {t.recurring_day}</span>
                  {!t.active && <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Oprit</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{t.description || "—"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="tabular text-sm font-medium text-right">
                  {ron(t.amount)}
                  <LeiHint amount={t.amount} doc={t} />
                </span>
                <button onClick={() => toggleActive(t)} className="text-xs text-slate-500 hover:text-[#0047FF]">{t.active ? "Oprește" : "Pornește"}</button>
                <button onClick={() => remove(t.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FixedCostsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "Chirie", amount: "", description: "", date: "", invoice_path: null, invoice_filename: "", vat_status: "cu_tva", original_amount: null, original_currency: null, rate_at_entry: null });
  const [loading, setLoading] = useState(false);

  async function load() {
    const { data } = await api.get("/fixed-costs");
    setItems(data);
  }
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("data:refresh", h);
    return () => window.removeEventListener("data:refresh", h);
  }, []);

  async function add(e) {
    e.preventDefault();
    if (!form.amount) return;
    setLoading(true);
    try {
      await api.post("/fixed-costs", {
        category: form.category,
        amount: Number(form.amount),
        description: form.description,
        invoice_path: form.invoice_path,
        date: form.date ? new Date(form.date).toISOString() : null,
        vat_status: form.vat_status,
        original_amount: form.original_amount,
        original_currency: form.original_currency,
        rate_at_entry: form.rate_at_entry,
        rate_locked: !!form.rate_locked,
      });
      setForm({ category: "Chirie", amount: "", description: "", date: "", invoice_path: null, invoice_filename: "", vat_status: "cu_tva", original_amount: null, original_currency: null, rate_at_entry: null });
      toast.success("Cost fix adăugat");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  async function pickInvoice(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, invoice_path: data.path, invoice_filename: data.filename });
      toast.success("Document atașat");
    } catch { toast.error("Upload eșuat"); }
    finally { e.target.value = ""; }
  }

  async function remove(id) {
    await api.delete(`/fixed-costs/${id}`);
    load();
  }

  const total = items.reduce((s, x) => s + (x.amount || 0), 0);

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthTotal = items
    .filter((x) => new Date(x.date) >= monthStart)
    .reduce((s, x) => s + (x.amount || 0), 0);

  return (
    <div className="space-y-3">
      <RecurringTemplates />

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-2">
        <div className="grid grid-cols-12 gap-2">
          <select data-testid="fc-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="col-span-12 sm:col-span-3 border border-slate-200 rounded-lg px-2 py-2 text-sm">
            {FIXED_COST_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <div className="col-span-12 sm:col-span-4">
            <DualMoneyInput
              testid="fc-money"
              value={form.amount}
              onChange={(v) => setForm({ ...form, amount: v })}
              onMeta={(m) => setForm((prev) => ({ ...prev, ...m }))}
            />
          </div>
          <div className="col-span-6 sm:col-span-2">
            <DateInput testid="fc-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
          </div>
          <input data-testid="fc-desc" placeholder="Descriere (opțional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-6 sm:col-span-2 border border-slate-200 rounded-lg px-2 py-2 text-sm" />
          <button data-testid="fc-add" className="col-span-12 sm:col-span-1 bg-[#0047FF] hover:bg-[#003BD3] text-white rounded-lg text-sm py-2 inline-flex items-center justify-center gap-1" disabled={loading}>
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="fc-vat" value={form.vat_status} onChange={(e) => setForm({ ...form, vat_status: e.target.value })} className="border border-slate-200 rounded-full px-3 py-1.5 text-xs bg-white">
            {VAT_STATUS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
          <button type="button" data-testid="fc-attach" onClick={() => document.getElementById("fc-invoice").click()} className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#0047FF] border border-slate-200 rounded-full px-3 py-1.5">
            <Paperclip className="w-3.5 h-3.5" /> {form.invoice_path ? "Schimbă documentul" : "Atașează document (PDF/foto)"}
          </button>
          {form.invoice_filename && <span className="text-xs text-emerald-700 truncate">✓ {form.invoice_filename}</span>}
          <input id="fc-invoice" type="file" accept="application/pdf,image/*" capture="environment" className="hidden" onChange={pickInvoice} />
        </div>
      </form>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Total</div>
          <div className="font-display text-xl font-semibold tabular">{ron(total)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Luna curentă</div>
          <div className="font-display text-xl font-semibold tabular text-[#0047FF]">{ron(monthTotal)}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="p-10 text-center">
            <Building2 className="w-10 h-10 mx-auto text-slate-300" />
            <div className="mt-3 font-display text-lg">Niciun cost fix</div>
            <div className="text-sm text-slate-500 mt-1">Adaugă chirie, utilități, salarii, leasing...</div>
          </div>
        )}
        {items.map((f) => (
          <div key={f.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50" data-testid={`fc-row-${f.id}`}>
            <div className="min-w-0">
              <div className="font-medium text-sm flex items-center gap-2">
                {f.category}
                {f.invoice_path && <span className="text-emerald-600"><Paperclip className="w-3 h-3" /></span>}
                {f.vat_status && <VatBadge status={f.vat_status} />}
                {f.recurring_template_id && <span className="text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Repeat className="w-2.5 h-2.5" />Auto</span>}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {f.description || "—"} · {formatDate(f.date)}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <InvoiceDownload path={f.invoice_path} />
              <div className="text-right">
                <span className="inline-flex items-center gap-1.5"><RateBadge doc={f} testid={`fixed-rate-badge-${f.id}`} /><span className="tabular text-sm font-medium">{ron(f.amount)}</span></span>
                <LeiHint amount={f.amount} doc={f} />
              </div>
              <button onClick={() => remove(f.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Expenses() {
  return (
    <div className="sm:pl-52 space-y-4" data-testid="expenses-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Cheltuieli</div>
        <h1 className="font-display text-3xl tracking-tight font-medium">Costuri</h1>
      </div>
      <Tabs defaultValue="cars">
        <TabsList className="bg-white border border-slate-200 rounded-full p-1">
          <TabsTrigger data-testid="tab-car-expenses" value="cars" className="rounded-full data-[state=active]:bg-[#0A0F1C] data-[state=active]:text-white">
            <Receipt className="w-3.5 h-3.5 mr-1.5" /> Pe mașini
          </TabsTrigger>
          <TabsTrigger data-testid="tab-fixed-costs" value="fixed" className="rounded-full data-[state=active]:bg-[#0A0F1C] data-[state=active]:text-white">
            <Building2 className="w-3.5 h-3.5 mr-1.5" /> Costuri fixe
          </TabsTrigger>
        </TabsList>
        <TabsContent value="cars" className="mt-4">
          <CarExpensesTab />
        </TabsContent>
        <TabsContent value="fixed" className="mt-4">
          <FixedCostsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
